import {type Point} from '../../../util/plotHelpers/types';
import {type NumericLine} from '../chartSeries';

/** One drawable line or one edge of a band. Both edges borrow the same geometry. */
export type SeriesEntry<T extends NumericLine = NumericLine> = {
  line: T;
  type: 'primary-series' | 'band-min' | 'band-max';
};

type SeriesIndices = {
  bandMinSeriesIndex?: number;
  bandMaxSeriesIndex?: number;
  primarySeriesIndex?: number;
};
export type SeriesById = Map<string, SeriesIndices>;

/** Index zero is reserved by uPlot. Bands precede lines; lookup uses drawable IDs. */
export type UPlotSeriesLayout<T extends NumericLine = NumericLine> = {
  seriesEntries: (SeriesEntry<T> | null)[];
  seriesById: SeriesById;
};

/** Full-fidelity and sampled bands store the two bounds in opposite fields. */
export function getBandLowerY(point: Point): number {
  return point.y0 != null && point.y0 < point.y ? point.y0 : point.y;
}

export function getBandUpperY(point: Point): number | null {
  if (point.y0 == null) {
    return null;
  }
  return point.y0 < point.y ? point.y : point.y0;
}

/**
 * Pair band edges by their own ID, even with missing descriptions or several
 * bands for one parent. A primary may borrow X coordinates from one associated
 * band only after getFacetedData verifies every prepared X coordinate matches.
 */
export function getSeriesEntries<T extends NumericLine>(
  lines: T[]
): UPlotSeriesLayout<T> {
  const entries: (SeriesEntry<T> | null)[] = [null];
  const seriesById: SeriesById = new Map();
  const primarySeriesLines: T[] = [];
  const bandSourceLines: T[] = [];
  for (const line of lines) {
    if (line.role === 'band') {
      if (line.data.length > 0) {
        bandSourceLines.push(line);
      }
    } else {
      primarySeriesLines.push(line);
    }
  }
  for (const line of bandSourceLines) {
    const indices = {
      bandMinSeriesIndex: entries.length,
      bandMaxSeriesIndex: entries.length + 1,
    };
    seriesById.set(line.seriesId, indices);
    // This parent lookup permits coordinate reuse; it never pairs band edges.
    if (line.parentSeriesId != null && !seriesById.has(line.parentSeriesId)) {
      seriesById.set(line.parentSeriesId, indices);
    }
    entries.push({type: 'band-min', line}, {type: 'band-max', line});
  }
  for (const line of primarySeriesLines) {
    const parentBand =
      line.role === 'primary' ? seriesById.get(line.seriesId) : undefined;
    seriesById.set(line.seriesId, {
      ...parentBand,
      primarySeriesIndex: entries.length,
    });
    entries.push({type: 'primary-series', line});
  }
  return {seriesEntries: entries, seriesById};
}
